<template>
  <div v-if="editor" class="tip-tap-editor">
    <editor-content :editor="editor" class="editor-content" />
    <cta-row
      overlay-modal
      no-spacing
      classes="tip-tap-editor__controls-wrapper bg-white border-bottom"
    >
      <upload-manager
        v-if="!isBuilderContext()"
        :upload-key="uploadKey"
        :uploaded-files="uploadedFiles"
        @uploading="val => $emit('uploading', val)"
        @uploaded="file => $emit('uploaded', file)"
        @removed="file => $emit('removeUploaded', file)"
      />

      <div class="tip-tap-editor__controls container w-100 py-1 px-2">
        <v-button
          x-small
          secondary
          :disabled="disabledBold"
          :active="editor.isActive('bold')"
          @click="toggleBold"
        >
          <v-icon icon="format_bold" />
        </v-button>
        <v-button
          x-small
          secondary
          :disabled="disabledItalic"
          :active="editor.isActive('italic')"
          @click="toggleItalic"
        >
          <v-icon icon="format_italic" />
        </v-button>
        <v-button
          x-small
          secondary
          :active="editor.isActive('bulletList')"
          @click="toggleBulletList"
        >
          <v-icon icon="format_list_bulleted" />
        </v-button>
        <v-button
          x-small
          secondary
          :active="editor.isActive('orderedList')"
          @click="toggleOrderedList"
        >
          <v-icon icon="format_list_numbered" />
        </v-button>
        <v-button
          x-small
          secondary
          @click="() => openFileDialog(SUPPORTED_VIDEO_MIME_TYPES)"
        >
          <v-icon icon="play_circle" />
        </v-button>
        <v-button x-small secondary @click="() => openFileDialog('image/*')">
          <v-icon icon="image" />
        </v-button>
      </div>
    </cta-row>
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2"
import Paragraph from "@tiptap/extension-paragraph"
import Document from "@tiptap/extension-document"
import Text from "@tiptap/extension-text"
import Bold from "@tiptap/extension-bold"
import Italic from "@tiptap/extension-italic"
import Link from "@tiptap/extension-link"
import OrderedList from "@tiptap/extension-ordered-list"
import BulletList from "@tiptap/extension-bullet-list"
import ListItem from "@tiptap/extension-list-item"
import History from "@tiptap/extension-history"
import Placeholder from "@tiptap/extension-placeholder"

import EventBus, { EVENTS } from "@/lib/event-bus"
import { isBuilderContext } from "@/lib/is-platform"
import { SUPPORTED_VIDEO_MIME_TYPES } from "@shared/upload/supported-video-types"

import CtaRow from "@/components/CtaRow"
import UploadManager from "@/components/UploadManager"

export default {
  components: {
    EditorContent,
    CtaRow,
    UploadManager
  },

  props: {
    content: { type: String, default: null },
    uploadedFiles: { type: Array, default: () => [] },
    placeholder: { type: String, default: "" }
  },

  data() {
    return {
      editor: null,
      uploadKey: this._uid,
      SUPPORTED_VIDEO_MIME_TYPES
    }
  },

  computed: {
    disabledBold() {
      return !this.editor
        .can()
        .chain()
        .focus()
        .toggleBold()
        .run()
    },

    disabledItalic() {
      return !this.editor
        .can()
        .chain()
        .focus()
        .toggleItalic()
        .run()
    }
  },

  mounted() {
    this.editor = new Editor({
      content: this.content,
      extensions: [
        Paragraph,
        Document,
        Text,
        Bold,
        Italic,
        OrderedList,
        BulletList,
        ListItem,
        Link,
        History,
        Placeholder.configure({
          placeholder: this.placeholder
        })
      ],
      parseOptions: {
        preserveWhitespace: "full"
      },
      onUpdate: ({ editor }) => {
        const html = editor.getHTML()

        this.$emit("input", this.isEmptyContent(html) ? "" : html)
      }
    })
  },

  beforeDestroy() {
    this.editor.destroy()
  },

  methods: {
    isBuilderContext,

    isEmptyContent(html) {
      let node = document.createElement("div")
      node.innerHTML = html

      return !this.hasVisibleContent(node)
    },

    hasVisibleContent(node) {
      // Check if node is a text node with non-whitespace text
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== "") {
        return true
      }
      // If node has child nodes, check them recursively
      for (let i = 0; i < node.childNodes.length; i++) {
        if (this.hasVisibleContent(node.childNodes[i])) {
          return true
        }
      }
      return false
    },

    toggleBold() {
      this.editor
        .chain()
        .focus()
        .toggleBold()
        .run()
    },

    toggleItalic() {
      this.editor
        .chain()
        .focus()
        .toggleItalic()
        .run()
    },

    toggleBulletList() {
      this.editor
        .chain()
        .focus()
        .toggleBulletList()
        .run()
    },

    toggleOrderedList() {
      this.editor
        .chain()
        .focus()
        .toggleOrderedList()
        .run()
    },

    openFileDialog(supportedFileTypes) {
      EventBus.$emit(EVENTS.OPEN_FILE_DIALOG, {
        key: this.uploadKey,
        supportedFileTypes: supportedFileTypes
      })
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.ProseMirror {
  > * {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
}

.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: $color-ui--grey-50;
  pointer-events: none;
  height: 0;
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";

.tip-tap-editor__controls-wrapper {
  margin-bottom: calc(
    68px + env(safe-area-inset-bottom)
  ); // Height of cta row actions

  .v-cta-row__content {
    padding-left: 0;
    padding-right: 0;
  }
}

.tip-tap-editor__controls {
  border-top: 1px solid $color-ui--grey-20;
  background-color: $color-ui--white;
  opacity: 0;
  animation: fade-in 0.2s ease forwards 0.4s;

  .v-button {
    margin-right: 0.25rem;
    font-size: 15px;
    padding: 14px;

    &.--active {
      opacity: 0.3;
    }

    .v-icon {
      color: $color-ui--black;
    }
  }
}
</style>
